const ENCRYPTION_KEY = "12345678901234567890123456789012";
const ALGORITHM = "AES-CBC";
const IV_LENGTH = 16;

// Helper: string to ArrayBuffer
const strToBuffer = (str) => new TextEncoder().encode(str);

// Helper: hex to Uint8Array
const hexToBuffer = (hex) =>
  new Uint8Array(hex.match(/.{1,2}/g).map((b) => parseInt(b, 16)));

// Helper: ArrayBuffer to hex
const bufferToHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

// Helper: Import key
const importKey = async (keyStr) => {
  return crypto.subtle.importKey(
    "raw",
    strToBuffer(keyStr),
    { name: ALGORITHM },
    false,
    ["encrypt", "decrypt"]
  );
};

// Helper: Encrypt Token (random IV — different output each time)
export async function encryptToken(token) {
  const key = await importKey(ENCRYPTION_KEY);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encrypted = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    strToBuffer(token)
  );
  return `${bufferToHex(iv)}:${bufferToHex(encrypted)}`;
}

// Helper: Decrypt Token
export async function decryptToken(encryptedToken) {
  const [ivHex, encryptedHex] = encryptedToken.split(":");
  const key = await importKey(ENCRYPTION_KEY);
  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: hexToBuffer(ivHex) },
    key,
    hexToBuffer(encryptedHex)
  );
  return new TextDecoder().decode(decrypted);
}

// Helper: Encrypt Password (fixed IV — same input always gives same output for DB comparison)
export async function encryptPassword(password) {
  const key = await importKey(ENCRYPTION_KEY);
  const FIXED_IV = new Uint8Array(IV_LENGTH); // all zeros fixed IV
  const encrypted = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: FIXED_IV },
    key,
    strToBuffer(password)
  );
  return bufferToHex(encrypted);
}

// Helper: Decrypt Password (reverses encryptPassword using the same fixed IV)
export async function decryptPassword(encryptedPassword) {
  const key = await importKey(ENCRYPTION_KEY);
  const FIXED_IV = new Uint8Array(IV_LENGTH); // same all-zeros fixed IV
  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: FIXED_IV },
    key,
    hexToBuffer(encryptedPassword)
  );
  return new TextDecoder().decode(decrypted);
}